/**
 * Both approved revisions open at the same editor step, side by side.
 *
 * One shared step navigation rather than two: the whole point is that the two
 * panes are looking at the same thing, and two selects would let them drift.
 *
 * @module components/portfolio/comparison/RevisionSideBySide
 */

import React, { useMemo, useState } from "react";

import {
  COMPARISON_STEPS,
  StepContent,
} from "@/components/portfolio/comparison/RevisionStepContent";
import { curriculumRevisionLabel } from "@/components/shared/revision-labels";
import {
  Badge,
  Button,
  FormField,
  Heading,
  Icon,
  MutedText,
  Panel,
  SelectField,
} from "@/components/ui";
import type { CurriculumRevision } from "@/server/types";

function RevisionPane({
  revision,
  stepLabel,
  step,
}: {
  revision: CurriculumRevision;
  stepLabel: string;
  step: string;
}): React.ReactElement {
  return (
    <Panel
      tone="inset"
      headingLevel={3}
      title={revision.institutionalLabel}
      description={curriculumRevisionLabel(`${revision.major}.${revision.minor}`)}
      // The heading is the revision's label alone, so two panes of this shape
      // beside each other name themselves identically to a reader moving by
      // region. This says what they are where a region name is read.
      aria-label={`${revision.institutionalLabel} locked programme workspace`}
      actions={
        <Badge tone="ghost">
          <Icon name="lock" aria-hidden /> Locked
        </Badge>
      }
      className="min-w-0"
    >
      <Heading level={4} size="md" className="mb-4">
        {stepLabel}
      </Heading>
      <StepContent programme={revision.data} step={step} />
    </Panel>
  );
}

export function RevisionSideBySide({
  fromRevision,
  toRevision,
}: {
  fromRevision: CurriculumRevision;
  toRevision: CurriculumRevision;
}): React.ReactElement {
  const steps = useMemo(
    () =>
      COMPARISON_STEPS.filter(
        (step) =>
          step.id !== "cas-picker" ||
          Boolean(fromRevision.data.casMajorAwardCode ?? toRevision.data.casMajorAwardCode),
      ),
    [fromRevision.data.casMajorAwardCode, toRevision.data.casMajorAwardCode],
  );
  const [stepId, setStepId] = useState(steps[0]?.id ?? "identity");
  const stepIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === stepId),
  );
  const step = steps[stepIndex] ?? steps[0]!;
  return (
    <section aria-labelledby="side-by-side-heading">
      <Heading level={2} id="side-by-side-heading">
        Side-by-side programme workspace
      </Heading>
      <MutedText className="mt-1">
        Both workspaces are locked. Use one set of steps to move through both revisions.
      </MutedText>
      <div className="sticky top-28 z-30 mt-4 border-y border-base-300 bg-base-100/95 py-3 backdrop-blur">
        {/* Not `Pagination`: the step select sits between the two arrows and
            names the step you would jump to, which that control has no slot
            for. Its mechanisms are copied rather than reinvented, so the two
            behave alike where they overlap. */}
        <div className="flex flex-wrap items-end gap-3">
          <Button
            variant="ghost"
            size="sm"
            // Dimmed rather than disabled, as `Pagination` is: the browser
            // blurs a control the moment it becomes disabled, so the press
            // that reaches step one would drop focus to the body.
            aria-disabled={stepIndex === 0 || undefined}
            onClick={stepIndex === 0 ? undefined : () => setStepId(steps[stepIndex - 1]!.id)}
            aria-label="Previous comparison step"
            icon="caret-left"
            square
          />
          <FormField label="Programme step" htmlFor="side-by-side-step" className="min-w-64 flex-1">
            <SelectField
              id="side-by-side-step"
              value={step.id}
              onChange={setStepId}
              options={steps.map(({ id, label }) => ({ value: id, label }))}
              data-testid="side-by-side-step"
            />
          </FormField>
          {/* Announced on change: the press replaces both panes, and this is
              the only thing that says where it landed. The region is the
              wrapper, which stands whatever the step, rather than the text,
              which a reader is not told about when it arrives with its own
              region. */}
          <span aria-live="polite" className="pb-2">
            <MutedText as="span">
              Step {stepIndex + 1} of {steps.length}
            </MutedText>
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-disabled={stepIndex === steps.length - 1 || undefined}
            onClick={
              stepIndex === steps.length - 1 ? undefined : () => setStepId(steps[stepIndex + 1]!.id)
            }
            aria-label="Next comparison step"
            icon="caret-right"
            square
          />
        </div>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <RevisionPane revision={fromRevision} stepLabel={step.label} step={step.id} />
        <RevisionPane revision={toRevision} stepLabel={step.label} step={step.id} />
      </div>
    </section>
  );
}
